//
/* GS://PROTOCOLO
 * Arquivo: src/pages/CustomersPage.js
 * Versão: 2.2 – UI FIX: Destaque para Aniversariantes do Mês
 * Data: 13/01/2026
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - ADICIONADO: Ícone 'Gift' e lógica `isBirthdayMonth` para identificar aniversariantes.
 * - MODIFICADO: A linha da tabela agora recebe a cor `bg-pink-50` se for o mês de aniversário do cliente.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getCustomers, addCustomer, updateCustomer, deleteCustomer } from '../services/firestoreService';
import CustomerForm from '../components/forms/CustomerForm';
import AdjustPointsForm from '../components/forms/AdjustPointsForm';
import AdminSubscriptionForm from '../components/forms/AdminSubscriptionForm';
// [GS-UI]: Adicionado ícone Gift
import { PlusCircle, Loader2, Trash2, Pencil, Star, DollarSign, Crown, Eye, Gift } from 'lucide-react';

const CustomersPage = ({ openModal, closeModal }) => {
    const [customers, setCustomers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchCustomers = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await getCustomers();
            setCustomers(data);
        } catch (error) {
            console.error("Erro ao carregar clientes:", error);
            if (openModal) {
                openModal(
                    <p className="text-red-500">Não foi possível carregar os clientes: {error.message}</p>,
                    "Erro de Carregamento",
                    "lg"
                );
            }
        } finally {
            setIsLoading(false);
        }
    }, [openModal]);

    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    const handleSave = async (customerData, id) => {
        try {
            const dataToSave = { ...customerData };

            if (id) {
                await updateCustomer(id, dataToSave);
            } else {
                await addCustomer(dataToSave);
            }
            fetchCustomers();
            closeModal();
        } catch (error) {
            console.error("Erro ao salvar cliente:", error);
            if (openModal) {
                openModal(
                    <p className="text-red-500">Não foi possível salvar o cliente: {error.message}</p>,
                    "Erro ao Salvar",
                    "lg"
                );
            }
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Tem certeza que deseja excluir este cliente?")) {
            try {
                await deleteCustomer(id);
                fetchCustomers();
            } catch (error) {
                console.error("Erro ao deletar cliente:", error);
                if (openModal) {
                    openModal(
                        <p className="text-red-500">Não foi possível excluir o cliente: {error.message}</p>,
                        "Erro ao Excluir",
                        "lg"
                    );
                }
            }
        }
    };

    const handleEdit = (customer) => {
        openModal(
            <CustomerForm onClose={closeModal} onSave={handleSave} customerData={customer} />,
            "Editar Cliente",
            '2xl'
        );
    };

    const handleAdjustPoints = (customer) => {
        const userPayload = {
            uid: customer.id,
            displayName: customer.name,
            email: customer.email
        };

        openModal(
            <AdjustPointsForm
                user={userPayload}
                onClose={closeModal}
                onSuccess={() => {
                    fetchCustomers();
                }}
            />,
            `Ajuste Financeiro (Wallet): ${customer.name}`,
            'lg'
        );
    };

    const formatPlanName = (customer) => {
        const activePlan = customer.activeSubscription?.planName;
        const rootSubscriptionPlan = customer.subscriptionPlan;

        let determinedPlanName = 'Aspirante';

        if (activePlan && activePlan !== 'Aspirante') {
            determinedPlanName = activePlan;
        } else if (rootSubscriptionPlan && rootSubscriptionPlan !== 'Aspirante') {
            determinedPlanName = rootSubscriptionPlan;
        } else if (customer.planDetails?.id && customer.planDetails.id !== 'Aspirante') {
            determinedPlanName = customer.planDetails.id;
        }

        let finalPlanName = String(determinedPlanName || '').replace(/"/g, '').trim();
        if (finalPlanName.startsWith('Clube ')) {
            finalPlanName = finalPlanName.replace('Clube ', '');
        } else if (finalPlanName.startsWith('Plano ')) {
            finalPlanName = finalPlanName.replace('Plano ', '');
        }
        finalPlanName = finalPlanName.charAt(0).toUpperCase() + finalPlanName.slice(1);

        return finalPlanName === 'Aspirante' ? 'Padrão (Aspirante)' : `Clube ${finalPlanName}`;
    };

    const handleSubscription = (customer) => {
        const currentPlanName = formatPlanName(customer);

        const userPayload = {
            uid: customer.id,
            displayName: customer.name,
            email: customer.email,
            currentPlan: currentPlanName
        };

        openModal(
            <AdminSubscriptionForm
                user={userPayload}
                onClose={closeModal}
                onSuccess={() => {
                    fetchCustomers();
                }}
            />,
            `Gestão de Clube (Assinatura)`,
            'lg'
        );
    };

    const handleNew = () => {
        openModal(
            <CustomerForm onClose={closeModal} onSave={handleSave} />,
            "Novo Cliente",
            '2xl'
        );
    };

    // ===========
    // Detalhes (somente leitura): Aniversário (DD/MM) + Rua/Bairro
    // ===========

    const formatBirthdayDDMM = (birthDateRaw) => {
        if (!birthDateRaw) return '—';

        if (typeof birthDateRaw === 'string') {
            const s = birthDateRaw.trim();
            const m = s.match(/^(\d{1,2})[/-](\d{1,2})(?:[/-]\d{2,4})?$/);
            if (m) {
                const dd = String(m[1]).padStart(2, '0');
                const mm = String(m[2]).padStart(2, '0');
                return `${dd}/${mm}`;
            }
            return '—';
        }

        try {
            const d = birthDateRaw?.toDate ? birthDateRaw.toDate() : new Date(birthDateRaw);
            if (Number.isNaN(d.getTime())) return '—';
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            return `${dd}/${mm}`;
        } catch {
            return '—';
        }
    };

    // [GS-UI]: Função auxiliar para verificar mês de aniversário
    const isBirthdayMonth = (birthDateRaw) => {
        if (!birthDateRaw) return false;
        try {
            let bDate = null;
            if (typeof birthDateRaw.toDate === 'function') {
                bDate = birthDateRaw.toDate();
            } else if (birthDateRaw instanceof Date) {
                bDate = birthDateRaw;
            } else if (typeof birthDateRaw === 'string') {
                const parts = birthDateRaw.match(/(\d+)/g);
                if (parts && parts.length >= 2) {
                   if (birthDateRaw.includes('/')) {
                       bDate = new Date();
                       bDate.setDate(parseInt(parts[0]));
                       bDate.setMonth(parseInt(parts[1]) - 1);
                   } else {
                       bDate = new Date(birthDateRaw);
                   }
                }
            }
            
            if (bDate && !isNaN(bDate.getTime())) {
                const currentMonth = new Date().getMonth();
                return bDate.getMonth() === currentMonth;
            }
            return false;
        } catch {
            return false;
        }
    };

    const formatStreetNeighborhood = (customer) => {
        const street = customer?.defaultAddress?.street?.trim();
        const neighborhood = customer?.defaultAddress?.neighborhood?.trim();

        if (street && neighborhood) return `${street} — ${neighborhood}`;
        if (street) return street;
        if (neighborhood) return neighborhood;

        const legacyFull = customer?.address?.fullAddress?.trim();
        if (legacyFull) return legacyFull;

        return '—';
    };

    const CustomerDetailsModal = ({ customer }) => {
        const birthday = useMemo(() => formatBirthdayDDMM(customer?.birthDate), [customer]);
        const streetNeighborhood = useMemo(() => formatStreetNeighborhood(customer), [customer]);

        const city = customer?.defaultAddress?.city?.trim();
        const state = customer?.defaultAddress?.state?.trim();
        const cep = customer?.defaultAddress?.cep?.trim();

        const cityUf = city ? `${city}${state ? `/${state}` : ''}` : null;

        return (
            <div className="space-y-4 max-w-3xl">
                {/* Cabeçalho compacto */}
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
                        <div>
                            <p className="text-xs text-gray-500">Cliente</p>
                            <p className="text-base font-semibold text-gray-900">{customer?.name || '—'}</p>
                        </div>
                        <div className="text-xs text-gray-600">
                            <span className="font-medium">Email:</span> {customer?.email || '—'}
                        </div>
                    </div>
                </div>

                {/* FIX PRINCIPAL: items-start para não esticar os cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 self-start">
                        <p className="text-[11px] uppercase text-gray-500 font-semibold tracking-wide">
                            Aniversário (DD/MM)
                        </p>
                        <p className="mt-1 text-xl font-bold text-gray-900 leading-tight">
                            {birthday}
                        </p>
                        <p className="mt-2 text-[11px] text-gray-500">Fonte: users.birthDate</p>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 self-start">
                        <p className="text-[11px] uppercase text-gray-500 font-semibold tracking-wide">
                            Rua / Bairro
                        </p>
                        <p className="mt-1 text-sm font-semibold text-gray-900 leading-snug break-words">
                            {streetNeighborhood}
                        </p>

                        {/* Só mostra linha extra quando existir algo além de — */}
                        {(cityUf || cep) && (
                            <div className="mt-2 text-[11px] text-gray-700 space-y-1">
                                {cityUf && (
                                    <div>
                                        <span className="font-medium">Cidade/UF:</span> {cityUf}
                                    </div>
                                )}
                                {cep && (
                                    <div>
                                        <span className="font-medium">CEP:</span> {cep}
                                    </div>
                                )}
                            </div>
                        )}

                        <p className="mt-2 text-[11px] text-gray-500">Fonte: users.defaultAddress</p>
                    </div>
                </div>

                <div className="flex justify-end pt-1">
                    <button
                        type="button"
                        onClick={closeModal}
                        className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        );
    };

    const handleDetails = (customer) => {
        if (!openModal) return;
        openModal(
            <CustomerDetailsModal customer={customer} />,
            "Detalhes do Cliente (Leitura)",
            "lg"
        );
    };

    return (
        <div className="p-8 bg-gray-50 min-h-screen font-sans">
            <div className="flex flex-col md:flex-row justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Clientes</h1>
                    <p className="text-sm text-gray-500 mt-1">Gerenciamento de base de clientes e assinaturas</p>
                </div>
                <button
                    onClick={handleNew}
                    className="mt-4 md:mt-0 flex items-center bg-brand-gold text-brand-black font-semibold px-4 py-2 rounded-lg shadow-md hover:bg-yellow-500 transition-colors"
                >
                    <PlusCircle className="h-5 w-5 mr-2" />
                    Novo Cliente
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <table className="w-full text-sm text-left text-gray-600">
                    <thead className="text-xs text-gray-600 uppercase bg-gray-100 border-b border-gray-200">
                        <tr>
                            <th scope="col" className="px-6 py-4 font-semibold">Nome</th>
                            <th scope="col" className="px-6 py-4 font-semibold">Email</th>
                            <th scope="col" className="px-6 py-4 font-semibold">Telefone</th>
                            <th scope="col" className="px-6 py-4 font-semibold">Saldo GP</th>
                            <th scope="col" className="px-6 py-4 font-semibold">Plano</th>
                            <th scope="col" className="px-6 py-4 text-center font-semibold">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {isLoading ? (
                            <tr>
                                <td colSpan="6" className="text-center p-10">
                                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-brand-gold" />
                                    <p className="mt-2 text-gray-400">Carregando clientes...</p>
                                </td>
                            </tr>
                        ) : customers.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="text-center p-10 text-gray-400">
                                    Nenhum cliente encontrado.
                                </td>
                            </tr>
                        ) : (
                            customers.map((customer) => {
                                // [GS-UI]: Verifica se é aniversariante do mês
                                const isBday = isBirthdayMonth(customer.birthDate);

                                return (
                                    <tr key={customer.id} className={`hover:bg-gray-50 transition-colors ${isBday ? 'bg-pink-50' : ''}`}>
                                        <td className="px-6 py-4 font-medium text-gray-900">
                                            <div className="flex items-center gap-2">
                                                {customer.name}
                                                {isBday && <Gift className="w-4 h-4 text-pink-500" title="Aniversariante do Mês" />}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">{customer.email}</td>
                                        <td className="px-6 py-4">{customer.phone}</td>

                                        <td className="px-6 py-4 font-bold text-gray-700">
                                            <div className="flex items-center gap-1">
                                                <Star size={12} className="text-brand-gold" />
                                                {customer.availablePoints ? customer.availablePoints.toLocaleString('pt-BR') : 0}
                                            </div>
                                        </td>

                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-800 border border-blue-100">
                                                {formatPlanName(customer)}
                                            </span>
                                        </td>

                                        <td className="px-6 py-4">
                                            <div className="flex items-center justify-center space-x-2">
                                                <button
                                                    onClick={() => handleDetails(customer)}
                                                    className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                                                    title="Ver Detalhes (Aniversário + Rua/Bairro)"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>

                                                <button
                                                    onClick={() => handleSubscription(customer)}
                                                    className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-full transition-colors"
                                                    title="Gerir Assinatura (Clube)"
                                                >
                                                    <Crown className="h-4 w-4" />
                                                </button>

                                                <button
                                                    onClick={() => handleAdjustPoints(customer)}
                                                    className="p-2 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 rounded-full transition-colors"
                                                    title="Ajustar Saldo (Wallet)"
                                                >
                                                    <DollarSign className="h-4 w-4" />
                                                </button>

                                                <button
                                                    onClick={() => handleEdit(customer)}
                                                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
                                                    title="Editar"
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </button>

                                                <button
                                                    onClick={() => handleDelete(customer.id)}
                                                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                                                    title="Excluir"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CustomersPage;